import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type SessionInfo } from '../lib/api.js';
import { PageHeader, SectionLabel, Card, Stat, StatusPill } from '../components/ui.js';

interface GbpProps {
  session: SessionInfo;
}

type Tab = 'posts' | 'reviews';

interface LatestPost {
  id: string;
  published_at: string | null;
  type: string;
  body_excerpt: string;
}

interface LatestReview {
  id: string;
  received_at: string;
  rating: number;
  author: string;
  reply_sent: boolean;
}

interface GbpActivityResponse {
  posts_last_30d: number;
  reviews_last_30d: number;
  replies_sent: number;
  avg_rating: number | null;
  latest_posts: LatestPost[];
  latest_reviews: LatestReview[];
}

export function GbpPage({ session }: GbpProps) {
  const [tab, setTab] = useState<Tab>('posts');
  const accountId = session.accounts[0]?.account_id ?? '';

  const { data, isLoading, isError } = useQuery<GbpActivityResponse>({
    queryKey: ['gbp-activity', accountId],
    queryFn: () => api.get<GbpActivityResponse>(`/api/dashboard/client/gbp/activity?account_id=${accountId}`),
    staleTime: 5 * 60_000,
    enabled: !!accountId,
  });

  return (
    <div className="space-y-12">
      <PageHeader
        eyebrow="Google Business Profile"
        title={<>Owned, monitored, <em className="text-cream/80">replied.</em></>}
        subtitle="Bloom posts updates, drafts review replies in your voice for one-click approval, and tracks activity across all locations."
      />

      {isLoading && (
        <div className="text-sm text-cream/40">Loading GBP data…</div>
      )}

      {isError && (
        <div className="rounded-2xl border border-cream/10 bg-cream/[0.02] p-6 text-sm text-cream/55">
          Couldn't load GBP data right now. Please refresh in a moment.
        </div>
      )}

      {!isLoading && !isError && data && (
        <>
          <section>
            <SectionLabel>Last 30 days</SectionLabel>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Stat label="Posts published" value={data.posts_last_30d} tone="ok" />
              <Stat label="Reviews received" value={data.reviews_last_30d} />
              <Stat label="Replies sent" value={data.replies_sent} tone={data.replies_sent > 0 ? 'ok' : 'neutral'} />
              <Stat label="Avg rating" value={data.avg_rating != null ? `${data.avg_rating.toFixed(1)} ★` : '—'} tone="ok" />
            </div>
          </section>

          <div className="flex items-center gap-1 border-b border-cream/[0.08] -mb-6">
            {(['posts', 'reviews'] as Tab[]).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                className={`px-4 py-3 text-[11px] uppercase tracking-[0.22em] transition-colors relative ${
                  tab === t ? 'text-cream' : 'text-cream/40 hover:text-cream/70'
                }`}
              >
                {t}
                {tab === t && <span className="absolute left-2 right-2 bottom-0 h-px bg-cream/85" />}
              </button>
            ))}
          </div>

          {tab === 'posts' && <PostsTab posts={data.latest_posts} />}
          {tab === 'reviews' && <ReviewsTab reviews={data.latest_reviews} />}
        </>
      )}

      {!isLoading && !isError && !data && (
        <div className="rounded-2xl border border-cream/10 bg-cream/[0.02] p-6 text-sm text-cream/55">
          No data yet — Bloom is collecting data for this account.
        </div>
      )}
    </div>
  );
}

function PostsTab({ posts }: { posts: LatestPost[] }) {
  if (posts.length === 0) {
    return (
      <div className="rounded-2xl border border-cream/10 bg-cream/[0.02] p-6 text-sm text-cream/55">
        No posts yet — Bloom will publish updates as the engine warms up.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {posts.map((p) => (
        <Card key={p.id}>
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <StatusPill tone={p.type === 'offer' ? 'info' : p.type === 'event' ? 'warn' : 'neutral'}>
                  {p.type}
                </StatusPill>
                {p.published_at && (
                  <span className="text-[11px] text-cream/45">
                    {new Date(p.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                )}
              </div>
              <div className="text-cream/80 text-sm leading-relaxed">{p.body_excerpt}</div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

function ReviewsTab({ reviews }: { reviews: LatestReview[] }) {
  if (reviews.length === 0) {
    return (
      <div className="rounded-2xl border border-cream/10 bg-cream/[0.02] p-6 text-sm text-cream/55">
        No reviews yet — they will appear here as they come in.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {reviews.map((r) => (
        <Card key={r.id}>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="text-cream/85 text-sm">{r.author}</span>
              <span className="text-amber-300/85 tabular-nums">
                {'★'.repeat(r.rating)}<span className="text-cream/20">{'★'.repeat(5 - r.rating)}</span>
              </span>
              <span className="text-[11px] text-cream/45">
                {new Date(r.received_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
            </div>
            <StatusPill tone={r.reply_sent ? 'ok' : 'warn'}>
              {r.reply_sent ? 'replied' : 'awaiting'}
            </StatusPill>
          </div>
        </Card>
      ))}
    </div>
  );
}
